import { Injectable } from '@angular/core';
import { CollectionPaths } from '@models/collections';
import {
	GlobalFormsKeywords,
	GlobalMortgageFormTemplate,
} from '@models/global-forms';
import { Selector, State } from '@ngxs/store';
import {
	FirestoreStateBaseForGlobalWithSubscription,
	GlobalStateModel,
} from '@state/FirestoreStateBaseForGlobal';
import { MatterMortgagesState } from '../matter-mortgages.state';
import {
	MatterMortgage,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { DEFAULT_STATE_BASE } from '@state/FirestoreStateBase';
import { where } from '@angular/fire/firestore';

type MatterMortgageFormTemplatesStateModelItem = GlobalMortgageFormTemplate[];

export interface MatterMortgageFormTemplatesStateModel
	extends GlobalStateModel {
	items: MatterMortgageFormTemplatesStateModelItem;
}

const DEFAULT_STATE: MatterMortgageFormTemplatesStateModel = {
	...DEFAULT_STATE_BASE,
	items: [],
};

@State<MatterMortgageFormTemplatesStateModel>({
	name: 'matterMortgageFormTemplates',
	defaults: DEFAULT_STATE,
})
@Injectable()
export class MatterMortgageFormTemplatesState extends FirestoreStateBaseForGlobalWithSubscription<
	GlobalMortgageFormTemplate,
	MatterMortgageFormTemplatesStateModel
> {
	constructor() {
		super(
			() => CollectionPaths.global_forms(),
			(payload) => ({
				items: payload as MatterMortgageFormTemplatesStateModelItem,
			}),
			() => DEFAULT_STATE,
			() => [where(`keywords.${GlobalFormsKeywords.MORTGAGE}`, '==', true)],
		);
	}

	@Selector([MatterMortgageFormTemplatesState])
	static getFormTemplates(
		state: MatterMortgageFormTemplatesStateModel,
	): GlobalMortgageFormTemplate[] {
		return state.items;
	}

	@Selector([
		MatterMortgageFormTemplatesState,
		MatterMortgagesState.getSelectedMortgage,
	])
	static getMortgageFormTemplates(
		state: MatterMortgageFormTemplatesStateModel,
		selectedMortgage: MatterMortgageWithAssociations,
	): GlobalMortgageFormTemplate[] {
		const lender = getMortgagePrimaryLender(selectedMortgage);

		if (!selectedMortgage?.chargeType || !lender) {
			return [];
		}

		return state.items.filter(
			(template) =>
				(template.labels.lender === lender?.id ||
					template.labels.lender === lender?.referenceId) &&
				template.labels.chargeTypes?.includes(
					(selectedMortgage?.chargeType ?? '').toUpperCase(),
				),
		);
	}

	@Selector([
		MatterMortgageFormTemplatesState.getFormTemplates,
		MatterMortgagesState.getAllMortgages,
	])
	static getMatterMortgageFormTemplates(
		formTemplates: GlobalMortgageFormTemplate[],
		allMortgages: MatterMortgage[],
	): GlobalMortgageFormTemplate[] {
		const mortgageParamsMapped = allMortgages.map((mortgage) => {
			const lender = getMortgagePrimaryLender(mortgage);
			return {
				chargeType: mortgage.chargeType,
				lenderId: lender?.referenceId || lender?.id,
			};
		});

		return formTemplates.filter((template) =>
			mortgageParamsMapped.some(
				(mortgage) =>
					(template.labels.lender === mortgage.lenderId ||
						template.labels.lender === mortgage.lenderId) &&
					template.labels.chargeTypes?.includes(
						(mortgage.chargeType ?? '').toUpperCase(),
					),
			),
		);
	}

	@Selector([MatterMortgageFormTemplatesState.getMatterMortgageFormTemplates])
	static getMortgageFormTemplateById(
		formTemplates: GlobalMortgageFormTemplate[],
	): (id: string) => GlobalMortgageFormTemplate {
		return (id: string) => formTemplates.find((template) => template.id === id);
	}

	@Selector([MatterMortgageFormTemplatesState.getMortgageFormTemplates])
	static getNewFlaggedMortgageForms(
		formTemplates: GlobalMortgageFormTemplate[],
	): GlobalMortgageFormTemplate[] {
		return formTemplates.filter(
			// TODO: fix newFlagExpiryDate handling SC-23167
			(template) => !!template.labels.newFlagExpiryDate && false,
			// template.labels.newFlagExpiryDate.toDate() > new Date(),
		);
	}

	@Selector([MatterMortgageFormTemplatesState.getNewFlaggedMortgageForms])
	static hasNewFlaggedMortgageForm(
		formTemplates: GlobalMortgageFormTemplate[],
	): boolean {
		return formTemplates?.length > 0;
	}

	@Selector([MatterMortgageFormTemplatesState.getNewFlaggedMortgageForms])
	static hasNewFlaggedMortgageFormSync(
		formTemplates: GlobalMortgageFormTemplate[],
	): boolean {
		return formTemplates?.some(
			(template) => template.labels.doesExpiryDateApplyToSync,
		);
	}
}
